import {TimeScale} from "@app/review/timescale";
import {deserialize, deserializeArray} from '../serializable';
import {ICroppable} from '../shared/croppable.interface';
import {User} from '../shared/user.service';
import {Tag} from '../tag/models';

export enum AssetType {
    Asset,
    Image,
    Video,
    Marmoset,
    Group,
    Review,
}

export const IconMap = new Map<AssetType, string>([
    [AssetType.Image, 'image'],
    [AssetType.Video, 'videocam'],
    [AssetType.Marmoset, 'view_in_ar'],
    [AssetType.Group, 'photo_library'],
    [AssetType.Review, 'pageview'],
]);

export const AssetColorMap = new Map<AssetType, string>([
    [AssetType.Image, '#7bdb56'],
    [AssetType.Video, '#db9456'],
    [AssetType.Marmoset, '#db56be'],
    [AssetType.Group, '#48aab5'],
    [AssetType.Review, '#5f61ed'],
]);

export class Asset {
    id: string;
    asset_type: AssetType;
    name: string;
    author: User;
    description: string;
    created: Date;
    modified: Date;
    tags: Tag[];
    tag_ids: string[];
    source: string;
    source_size: number;
    comment_count: number;
    like_count: number;
    view_count: number;
    hidden: boolean;
    deleted: boolean;
    custom_thumbnail: string;
    revision: number;
    is_ready: boolean;
    protected _thumbnail: string;

    get thumbnail(): string {
        return this._thumbnail || 'assets/missing_image.png';
    }

    set thumbnail(value) {
        this._thumbnail = value;
    }

    get publicTags(): Tag[] {
        return this.tags.filter((tag) => tag.category != 'artist');
    }

    equals(other: Asset): boolean {
        if (other === undefined || other === null) {
            return false;
        }

        return this.id === other.id;
    }

    isExpired(): boolean {
        let thumbnailUrl: URL;
        try {
            thumbnailUrl = new URL(this.thumbnail);
        } catch (e) {
            // URL was invalid
            return false;
        }

        const expires = thumbnailUrl.searchParams.get('Expires');
        if (expires) {
            return parseFloat(expires) < new Date().getTime() / 1000;
        }

        return false;
    }
}

export class ImageAsset extends Asset implements ICroppable {
    image: string;
    panoramic: boolean;
    width: number;
    height: number;
}

export class VideoAsset extends Asset implements ICroppable {
    video: string;
    video_thumbnail: string;
    poster: string;
    width: number;
    height: number;
    framerate: number;
    duration: number;

    get image(): string {
        return this.poster;
    }

    get frameCount(): number {
        return this.framerate * this.duration;
    }

    public timeToFrame(time: number): number {
        const timeScale = new TimeScale(this.frameCount, this.framerate);
        return timeScale.timeToFrame(time);
    }

    public frameToTime(frame: number): number {
        const timeScale = new TimeScale(this.frameCount, this.framerate);
        return timeScale.frameToTime(frame);
    }
}

export class MarmosetAsset extends Asset {}

export class GroupAsset extends Asset {
    children: Asset[];
    child_ids: string[];

    get thumbnail(): string {
        return this._thumbnail || 'assets/empty_review.png';
    }

    set thumbnail(value) {
        this._thumbnail = value;
    }
}

export class ReviewAsset extends Asset {
    children: Asset[];
    child_ids: string[];
    parent_stream_id: string;

    get thumbnail(): string {
        return this._thumbnail || 'assets/empty_review.png';
    }

    set thumbnail(value) {
        this._thumbnail = value;
    }
}

export const AssetMap = new Map<AssetType, any>([
    [AssetType.Asset, Asset],
    [AssetType.Image, ImageAsset],
    [AssetType.Video, VideoAsset],
    [AssetType.Marmoset, MarmosetAsset],
    [AssetType.Group, GroupAsset],
    [AssetType.Review, ReviewAsset],
]);

export function deserializeAsset(asset: Asset): Asset {
    if (asset === undefined || asset === null) {
        return null;
    }
    const typeClass = AssetMap.get(asset.asset_type);
    asset.tags = deserializeArray(Tag, asset.tags);
    return deserialize(typeClass, asset);
}

export function deserializeAssetArray(assets: Asset[]): Asset[] {
    return assets.map((asset) => deserializeAsset(asset));
}
